<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import AppIcon from './components/AppIcon.vue'
import BackLink from './components/BackLink.vue'

const route = useRoute()
const showShell = computed(() => route.meta.shell !== false)
const showBack = computed(() => showShell.value && typeof route.meta.backTo === 'string')
const isHome = computed(() => route.name === 'home')
const isLibrary = computed(() => route.name === 'library')
const isContentDetail = computed(() => route.name === 'content-detail')
const showHeader = computed(() => showShell.value && !showBack.value && !isHome.value)
const backTarget = computed(() => route.name === 'meeting-watch' ? `/meetings/${String(route.params.id)}` : String(route.meta.backTo ?? '/'))
const navigation = [
  { label: '首页', icon: 'home', to: '/', activeNames: ['home', 'services', 'medical', 'survey'] },
  { label: '学习', icon: 'library', to: '/library', activeNames: ['library', 'content-detail', 'media-detail'] },
  { label: '搜索', icon: 'search', to: '/search', accent: true, activeNames: ['search'] },
  { label: '会议', icon: 'calendar', to: '/meetings', activeNames: ['meetings', 'meeting-detail', 'meeting-watch'] },
  { label: '我的', icon: 'user', to: '/profile', activeNames: ['profile', 'messages', 'learning', 'consent-settings'] },
]

function isNavigationActive(item: (typeof navigation)[number]) {
  return item.activeNames.includes(String(route.name ?? ''))
}
</script>

<template>
  <div class="portal-app" :class="{ 'portal-app--plain': !showShell, 'portal-app--detail': showBack, 'portal-app--content-detail': isContentDetail, 'portal-app--home': isHome, 'portal-app--library': isLibrary }">
    <header v-if="showHeader" class="portal-header">
      <div class="portal-header__inner">
        <RouterLink class="roche-logo" to="/" aria-label="锐医同行首页"><img src="/roche-logo.svg?v=20260819" alt="" /></RouterLink>
        <nav class="desktop-nav" aria-label="主导航">
          <RouterLink v-for="item in navigation" :key="item.to" :to="item.to">
            {{ item.label }}
          </RouterLink>
        </nav>
        <div class="header-actions">
          <RouterLink class="header-icon" to="/messages" aria-label="消息中心">
            <AppIcon name="bell" />
            <span>3</span>
          </RouterLink>
          <RouterLink class="avatar" to="/profile" aria-label="个人中心">李</RouterLink>
        </div>
      </div>
    </header>

    <main :class="showShell ? 'portal-main' : 'portal-main portal-main--plain'">
      <RouterView v-slot="{ Component }">
        <div v-if="showBack" class="route-back-wrap"><BackLink :to="backTarget" label="返回" /></div>
        <component :is="Component" />
      </RouterView>
    </main>

    <nav v-if="showShell && !isContentDetail" class="mobile-nav" aria-label="移动端主导航">
      <RouterLink v-for="item in navigation" :key="item.to" :to="item.to" :class="{ 'mobile-nav__accent': item.accent, 'mobile-nav__item--active': isNavigationActive(item) }">
        <span class="mobile-nav__icon"><AppIcon :name="item.icon" :variant="isNavigationActive(item) ? 'solid' : 'outline'" /></span>
        <span>{{ item.label }}</span>
      </RouterLink>
    </nav>
  </div>
</template>

<style scoped>
.portal-app--detail .portal-main{min-height:100vh}
.portal-app--home{overflow-x:hidden}.portal-app--home .portal-main{min-height:100vh}
.route-back-wrap{position:relative;z-index:2;width:min(1180px,calc(100% - 40px));margin:0 auto;padding-top:28px}.route-back-wrap :deep(.app-back){margin-bottom:0}.route-back-wrap+.page-shell{padding-top:14px}:global(.route-back-wrap + .page-shell .detail-back){display:none!important}
.portal-app--content-detail .portal-main{padding-bottom:0}
.portal-app--content-detail .route-back-wrap{position:fixed;z-index:60;top:22px;left:max(22px,calc((100vw - 1180px) / 2 + 22px));width:auto;margin:0;padding:0}
.portal-app--content-detail .route-back-wrap :deep(.app-back){display:grid;width:42px;height:42px;min-height:42px;border:1px solid rgba(215,224,247,.78);border-radius:50%;background:rgba(255,255,255,.9);box-shadow:0 8px 24px rgba(35,64,126,.1);color:var(--rio-blue);backdrop-filter:blur(12px);place-items:center}
.portal-app--content-detail .route-back-wrap :deep(.app-back span){display:none}
.portal-app--content-detail .route-back-wrap+.page-shell{padding-top:36px}
@media(max-width:767px){.route-back-wrap{width:calc(100% - 40px);padding-top:20px}.route-back-wrap+.page-shell{padding-top:10px}}
@media(max-width:767px){.portal-app--content-detail .portal-main{padding-bottom:0}.portal-app--content-detail .route-back-wrap{top:16px;left:16px}.portal-app--content-detail .route-back-wrap :deep(.app-back){width:40px;height:40px;min-height:40px}.portal-app--content-detail .route-back-wrap+.page-shell{padding-top:20px}}
</style>
